import { useMemo } from 'react'
import {
  DAY_GRID_END_HOUR,
  DAY_GRID_START_HOUR,
  canBookSlot,
  dayGridHours,
  dayKey,
  isSameMonth,
  longDayLabel,
  monthBookableSlot,
  slotTimeLabel,
  weekdayLabels,
  type VisibleRange,
  type CalendarView,
} from '../../lib/appointmentCalendar'
import {
  canSeeAppointmentLocation,
  canSeeAppointmentNotes,
  statusLabel,
} from '../../lib/appointmentForm'
import { formatAppointmentWhen } from '../../lib/formatAppointmentWhen'
import type { Appointment } from '../../lib/api'

interface CalendarUser {
  id: string
  role: string
}

interface AppointmentCalendarProps {
  view: CalendarView
  cursor: Date
  range: VisibleRange
  appointments: Appointment[]
  canCreate: boolean
  canViewAll: boolean
  user: CalendarUser | null
  onOpen: (appointment: Appointment) => void
  onCreateAt: (day: Date, hours: number, minutes: number) => void
}

export function AppointmentCalendar({
  view,
  cursor,
  range,
  appointments,
  canCreate,
  canViewAll,
  user,
  onOpen,
  onCreateAt,
}: AppointmentCalendarProps) {
  const byDay = useMemo(() => {
    const grouped = new Map<string, Appointment[]>()
    range.days.forEach((day) => grouped.set(dayKey(day), []))
    for (const appointment of appointments) {
      const bucket = grouped.get(dayKey(new Date(appointment.starts_at)))
      if (bucket) bucket.push(appointment)
    }
    return grouped
  }, [appointments, range.days])

  if (view === 'day') {
    const day = range.days[0]
    const items = byDay.get(dayKey(day)) ?? []
    const outside = items.filter((appointment) => {
      const hour = new Date(appointment.starts_at).getHours()
      return hour < DAY_GRID_START_HOUR || hour >= DAY_GRID_END_HOUR
    })

    return (
      <div className="rounded-lg border border-slate-200 bg-white">
        {outside.length > 0 && (
          <div className="border-b border-slate-200 p-3">
            <h2 className="text-sm font-semibold text-slate-800">
              Outside {slotTimeLabel(DAY_GRID_START_HOUR)}–{slotTimeLabel(DAY_GRID_END_HOUR)}
            </h2>
            <ul className="mt-2 space-y-2">
              {outside.map((appointment) => (
                <li key={appointment.id}>
                  <AppointmentButton
                    appointment={appointment}
                    canViewAll={canViewAll}
                    user={user}
                    onOpen={onOpen}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
        <ul>
          {dayGridHours().map((hour) => {
            const slotItems = items.filter((appointment) => new Date(appointment.starts_at).getHours() === hour)
            const label = slotTimeLabel(hour)
            const bookable = canCreate && canBookSlot(day, hour, 0)
            return (
              <li
                key={hour}
                className="flex gap-3 border-b border-slate-100 px-3 py-2 last:border-b-0"
                onClick={() => {
                  if (bookable) onCreateAt(day, hour, 0)
                }}
              >
                <div className="w-24 shrink-0 pt-1">
                  {bookable ? (
                    <button
                      type="button"
                      aria-label={`Add appointment at ${label}`}
                      onClick={(event) => {
                        event.stopPropagation()
                        onCreateAt(day, hour, 0)
                      }}
                      className="text-xs font-medium text-slate-500 hover:text-slate-800"
                    >
                      {label}
                    </button>
                  ) : (
                    <span className="text-xs font-medium text-slate-500">{label}</span>
                  )}
                </div>
                <ul className="min-w-0 flex-1 space-y-2">
                  {slotItems.map((appointment) => (
                    <li key={appointment.id}>
                      <AppointmentButton
                        appointment={appointment}
                        canViewAll={canViewAll}
                        user={user}
                        onOpen={onOpen}
                      />
                    </li>
                  ))}
                </ul>
              </li>
            )
          })}
        </ul>
      </div>
    )
  }

  if (view === 'month') {
    const labels = weekdayLabels()
    return (
      <div className="overflow-x-auto">
        <div className="grid min-w-[720px] grid-cols-7 gap-2">
          {labels.map((label) => (
            <div key={label} className="px-2 text-xs font-medium text-slate-500">{label}</div>
          ))}
          {range.days.map((day) => {
            const items = byDay.get(dayKey(day)) ?? []
            const inMonth = isSameMonth(day, cursor)
            const slot = monthBookableSlot(day)
            const bookable = canCreate && slot !== null
            return (
              <section
                key={dayKey(day)}
                aria-label={longDayLabel(day)}
                onClick={() => {
                  if (bookable && slot) onCreateAt(day, slot.hours, slot.minutes)
                }}
                className={`min-h-28 rounded-lg border border-slate-200 p-2 ${inMonth ? 'bg-white' : 'bg-slate-50'}`}
              >
                {bookable && slot ? (
                  <button
                    type="button"
                    aria-label={`Add appointment on ${longDayLabel(day)}`}
                    onClick={(event) => {
                      event.stopPropagation()
                      onCreateAt(day, slot.hours, slot.minutes)
                    }}
                    className={`text-xs font-semibold ${inMonth ? 'text-slate-800' : 'text-slate-400'}`}
                  >
                    {day.getDate()}
                  </button>
                ) : (
                  <p className={`text-xs font-semibold ${inMonth ? 'text-slate-800' : 'text-slate-400'}`}>
                    {day.getDate()}
                  </p>
                )}
                <ul className="mt-1 space-y-1">
                  {items.map((appointment) => (
                    <li key={appointment.id}>
                      <AppointmentButton
                        appointment={appointment}
                        canViewAll={canViewAll}
                        user={user}
                        onOpen={onOpen}
                        compact
                      />
                    </li>
                  ))}
                </ul>
              </section>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <div className="grid min-w-[980px] grid-cols-7 gap-2">
        {range.days.map((day) => {
          const items = byDay.get(dayKey(day)) ?? []
          const outside = items.filter((appointment) => {
            const hour = new Date(appointment.starts_at).getHours()
            return hour < DAY_GRID_START_HOUR || hour >= DAY_GRID_END_HOUR
          })
          return (
            <section
              key={dayKey(day)}
              aria-label={longDayLabel(day)}
              className="rounded-lg border border-slate-200 bg-white"
            >
              <h2 className="border-b border-slate-100 px-2 py-2 text-sm font-semibold text-slate-800">
                {day.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
              </h2>
              {outside.length > 0 && (
                <div className="border-b border-slate-200 p-2">
                  <p className="text-xs font-semibold text-slate-800">
                    Outside {slotTimeLabel(DAY_GRID_START_HOUR)}–{slotTimeLabel(DAY_GRID_END_HOUR)}
                  </p>
                  <ul className="mt-2 space-y-2">
                    {outside.map((appointment) => (
                      <li key={appointment.id}>
                        <AppointmentButton
                          appointment={appointment}
                          canViewAll={canViewAll}
                          user={user}
                          onOpen={onOpen}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <ul>
                {dayGridHours().map((hour) => {
                  const slotItems = items.filter((appointment) => new Date(appointment.starts_at).getHours() === hour)
                  const label = slotTimeLabel(hour)
                  const bookable = canCreate && canBookSlot(day, hour, 0)
                  return (
                    <li
                      key={hour}
                      className="border-b border-slate-100 px-2 py-1 last:border-b-0"
                      onClick={() => {
                        if (bookable) onCreateAt(day, hour, 0)
                      }}
                    >
                      {bookable ? (
                        <button
                          type="button"
                          aria-label={`Add appointment at ${label} on ${longDayLabel(day)}`}
                          onClick={(event) => {
                            event.stopPropagation()
                            onCreateAt(day, hour, 0)
                          }}
                          className="text-xs font-medium text-slate-500 hover:text-slate-800"
                        >
                          {label}
                        </button>
                      ) : (
                        <span className="text-xs font-medium text-slate-500">{label}</span>
                      )}
                      {slotItems.length > 0 && (
                        <ul className="mt-1 space-y-1">
                          {slotItems.map((appointment) => (
                            <li key={appointment.id}>
                              <AppointmentButton
                                appointment={appointment}
                                canViewAll={canViewAll}
                                user={user}
                                onOpen={onOpen}
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  )
                })}
              </ul>
            </section>
          )
        })}
      </div>
    </div>
  )
}

function AppointmentButton({
  appointment,
  canViewAll,
  user,
  onOpen,
  compact = false,
}: {
  appointment: Appointment
  canViewAll: boolean
  user: CalendarUser | null
  onOpen: (appointment: Appointment) => void
  compact?: boolean
}) {
  const showLocation = canSeeAppointmentLocation(user, appointment) && !!appointment.location
  const showNotes = canSeeAppointmentNotes(user, appointment) && !!appointment.notes

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation()
        onOpen(appointment)
      }}
      className="w-full rounded-md bg-slate-50 px-2 py-2 text-left hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
    >
      <p className="text-xs font-semibold capitalize text-slate-800">{appointment.type}</p>
      {!compact && <p className="text-xs text-slate-600">{formatAppointmentWhen(appointment.starts_at)}</p>}
      {compact && (
        <p className="text-xs text-slate-600">
          {new Date(appointment.starts_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
        </p>
      )}
      <p className="text-xs text-slate-500">{statusLabel(appointment.status)}</p>
      {canViewAll && appointment.assigned_user && (
        <p className="text-xs text-slate-500">{appointment.assigned_user.name}</p>
      )}
      {showLocation && <p className="text-xs text-slate-600">{appointment.location}</p>}
      {showNotes && <p className="whitespace-pre-wrap text-xs text-slate-600">{appointment.notes}</p>}
    </button>
  )
}
